<script setup lang="ts">
import { Translation } from 'vue-i18n';

import { AppImagesLoader, AppButton } from '@libs/ui';

import type { StaticSuggestedActivityCardData } from '../../../models';

interface StaticSuggestedActivitiesBannerProps {
  cards: StaticSuggestedActivityCardData[];
  destination: string;
  ingredientIndex: number;
}
const props = defineProps<StaticSuggestedActivitiesBannerProps>();

interface StaticSuggestedActivitiesBannerEmits {
  (event: 'show-activities'): void;
  (event: 'open-activity', activityCode: string): void;
}
const emit = defineEmits<StaticSuggestedActivitiesBannerEmits>();
</script>

<template>
  <div class="static-suggested-activities-banner">
    <div class="header">
      <Translation
        class="title"
        keypath="planner.activity_suggestions.things_to_do"
        tag="span"
      >
        <template #destination>
          <span class="destination">{{ props.destination }}</span>
        </template>
      </Translation>
      <AppButton
        class="see-activities-button"
        type="secondary"
        :state="props.cards.length ? 'enabled' : 'disabled'"
        size="medium"
        @click="emit('show-activities')"
      >
        {{ $t('activities_banner.button') }}
      </AppButton>
    </div>
    <div class="activities-wrapper">
      <div
        v-for="suggestedActivity in props.cards"
        :key="suggestedActivity.productCode"
        class="activity-card"
        @click="emit('open-activity', suggestedActivity.productCode)"
      >
        <AppImagesLoader
          class="image"
          :urls="[suggestedActivity.imageUrl]"
          img-type="contain"
          :alt="suggestedActivity.name"
        />
        <span class="name">
          {{ suggestedActivity.name }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.static-suggested-activities-banner {
  --margin-bottom: var(--spacing-32);

  display: flex;
  margin-bottom: var(--margin-bottom);
  gap: var(--spacing-32);
  padding: var(--spacing-24) 20px var(--spacing-24) 74px;
  background-color: var(--color-white);
  border-top: 1px solid var(--color-scrollbar);
  border-bottom: 1px solid var(--color-scrollbar);
  position: relative;

  @include for-less-or-equal-than-599-px {
    padding: var(--spacing-16);
  }
  .header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: var(--spacing-24);
    min-width: 230px;
    @include for-less-or-equal-than-599-px {
      gap: var(--spacing-16);
    }
    .title {
      @include font-label-xl-bold;
      color: var(--color-secondary-60);
      @include for-less-or-equal-than-599-px {
        @include font-label-l-bold;
      }
      .destination {
        display: block;
        margin-top: var(--spacing-8);
        @include font-heading-2;
        color: var(--color-primary-40);
        @include for-less-or-equal-than-599-px {
          @include font-heading-3;
        }
      }
    }
    .see-activities-button {
      width: fit-content;
    }
  }
  .activities-wrapper {
    display: grid;
    gap: var(--spacing-12);
    grid-template-columns: 1fr 1fr 1fr;

    @include for-less-or-equal-than-1200-px {
      grid-template-columns: 1fr 1fr;
      .activity-card:last-child {
        display: none;
      }
    }

    @include for-less-or-equal-than-599-px {
      display: none;
    }
    .activity-card {
      display: flex;
      gap: var(--spacing-8);
      flex-direction: column;
      padding: var(--spacing-12);
      height: 100%;
      border-radius: var(--border-radius-12);
      border: 1px solid var(--color-scrollbar);

      @include on-hover {
        cursor: pointer;
      }
      .image {
        object-fit: cover;
        border-radius: var(--border-radius-8);
        aspect-ratio: 5/4;
        width: 100%;
        height: auto;
      }
      .name {
        color: var(--color-black);
        @include font-label-l-bold;

        white-space: normal;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        overflow: hidden;
      }
    }
  }
  &::after {
    content: '';
    height: var(--margin-bottom);
    width: 0px;
    border-right: 1px dashed var(--color-secondary-50);
    position: absolute;
    top: 100%;
    left: 49px;

    @include for-less-or-equal-than-599-px {
      left: 37px;
    }
  }
}
</style>
